import { FileResult } from '@imagise/entities';
import { EditorCanvasViewState } from './editor-domain.models';

export function isGifSource(
  mimeType: string | null | undefined,
  fileName: string | null | undefined,
  url: string | null | undefined,
): boolean {
  return (
    mimeType?.toLowerCase() === 'image/gif' ||
    Boolean(fileName?.toLowerCase().endsWith('.gif')) ||
    Boolean(url?.toLowerCase().startsWith('data:image/gif'))
  );
}

/**
 * The browser can play the original GIF natively only while nothing is edited:
 * any committed edit or a non-trivial draft switches the preview to the
 * client-side frame player / static canvas.
 */
export function canShowNativeAnimatedSourcePreview(
  viewState: EditorCanvasViewState,
  imageFile: FileResult | null,
  activeImageUrl: string | null,
): boolean {
  if (viewState.committedEdits.length > 0) {
    return false;
  }

  const draft = viewState.draft;
  if (!draft) {
    return isGifSource(imageFile?.SourceMimeType ?? null, imageFile?.FileName ?? '', activeImageUrl);
  }

  if (draft.kind !== 'animation' || draft.params.frames.length !== 1) {
    return false;
  }

  const [frame] = draft.params.frames;
  return isGifSource(frame.mimeType, frame.name, frame.previewUrl);
}

/** Picks the best URL for playing the untouched source GIF natively. */
export function getNativeAnimatedSourceUrl(file: FileResult | null): string | null {
  if (!file) {
    return null;
  }

  if (file.LocalObjectUrl) {
    return file.LocalObjectUrl;
  }

  if (file.DownloadUrl) {
    return file.DownloadUrl;
  }

  if (file.UploadFileName) {
    return file.UploadFileName;
  }

  if (file.SourceBase64) {
    return `data:${file.SourceMimeType || 'image/gif'};base64,${file.SourceBase64}`;
  }

  return null;
}
